import { useRef } from "react";
import { createTrackingEmitterHooks } from "@b2bportal/core-tracking";
import type { Funnel } from "@b2bportal/core-types";
import type { RbcRawEvent } from "./types";

/**
 * Hook to emit RBC-specific raw tracking events
 *
 * Usage:
 * ```typescript
 * const emitEvent = useEmitRbcRawEvent(RbcAuthRawEventName.CommonAuth);
 * emitEvent({ properties: { cpc_code: 'CPC' } });
 * ```
 */
export const {
  useEmitRawEvent: useEmitRbcRawEvent,
  useEmitRawEventOnce: useEmitRbcRawEventOnce,
} = createTrackingEmitterHooks<RbcRawEvent>();

const STORAGE_KEY = "rbc_current_funnel";

/**
 * Hook to determine if a funnel entry event should be tracked.
 * Tracks funnel transitions by comparing current funnel vs sessionStorage.
 *
 * Returns true when:
 * - First visit to any funnel (no previous funnel stored)
 * - Transitioning from a different funnel (e.g., cars → hotels)
 *
 * Returns false when:
 * - Navigating within the same funnel
 * - Page refresh (sessionStorage persists)
 *
 * @param currentFunnel - The current funnel (e.g., Funnel.Lodging, Funnel.Ground)
 * @returns Whether the entry event should fire
 */
export const useTrackFunnelEntry = (
  currentFunnel: Funnel | undefined
): boolean => {
  const hasInitializedRef = useRef<boolean>(false);
  const shouldTrackRef = useRef<boolean>(false);

  // Only run the check once per hook instance
  if (!hasInitializedRef.current && currentFunnel) {
    hasInitializedRef.current = true;

    const storedFunnel = sessionStorage.getItem(STORAGE_KEY);
    const isFirstEntry = storedFunnel === null;
    const isFunnelTransition =
      storedFunnel !== null && storedFunnel !== currentFunnel;
    shouldTrackRef.current = isFirstEntry || isFunnelTransition;

    // Only update sessionStorage if we're tracking this entry
    if (shouldTrackRef.current) {
      sessionStorage.setItem(STORAGE_KEY, currentFunnel);
    }
  }

  return shouldTrackRef.current;
};
